import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { useForm, useFormState } from 'react-final-form';
import { useRouter } from 'next/router';
import Icon from '../../Icon';
import { fromObjToUrl, getCategoriesData } from '../../../utils/helpers/search';
import {
  CategoriesContainer,
  CategoryContainer,
  CategoryLinkContainer,
  CategoryText
} from './styled';
import { NewSearchCategoriesProps, NewSearchCategoryProps } from './types';
import { useStaticData } from '../../../hooks/useStaticData';
import { getInitialCategoriesLinks } from '../../../utils/helpers/getInitialCategoriesLinks';
import { getCategories, getLocalDataQuery } from '../../../api/queries';
import { LocalDataQuery } from '../../../graphql/graphql';
import { fillCategoriesWithData } from '../../../utils/helpers/fillCategoriesWithData';
import { BUILDING_TYPE_ID } from '../../../utils/helpers/constants';

const NewSearchCategory = ({
  icon,
  text,
  onClick,
  stat,
  isSelected,
  link
}: NewSearchCategoryProps) => {
  const Component =
    typeof link === 'string' ? CategoryLinkContainer : CategoryContainer;

  return (
    <Component
      data-cy={stat}
      data-stat={stat}
      onClick={onClick}
      onKeyPress={onClick}
      $isSelected={isSelected}
      href={link?.toString()}
      locale={false}
    >
      <Icon
        width={24}
        height={24}
        color="#fff"
        indent={false}
        IconComponent={icon}
      />
      <CategoryText tag="body_1">{text}</CategoryText>
    </Component>
  );
};

export const NewSearchCategories = ({
  onClick,
  selectedValue,
  categories,
  asLinks = false
}: NewSearchCategoriesProps) => {
  const router = useRouter();
  const { change } = useForm();
  const { values } = useFormState();

  const { data } = useStaticData(getCategories, !!categories?.length);
  const {
    t,
    i18n: { language }
  } = useTranslation();

  const categoriesData = (categories || getCategoriesData({ data, language }))
    .flatMap(category =>
      category.id === BUILDING_TYPE_ID.FLAT_ALL
        ? category.children || []
        : category
    )
    .filter(category => category.id !== BUILDING_TYPE_ID.FLAT_ALL);

  const filledCategories = fillCategoriesWithData(categoriesData, false, t);

  const { data: localData } = useQuery<LocalDataQuery>(getLocalDataQuery, {
    variables: { id: values.city_id },
    skip: !asLinks
  });

  const handleClick = (value: string) => {
    if (onClick) {
      onClick(value);
    } else {
      change?.('category_id', value);
      change?.('category_touched', true);
    }
  };

  const initialCategoriesLinks = getInitialCategoriesLinks(
    router.asPath === '/kiraye'
  );

  const generateCategoryUrl = (categoryId: string) => {
    if (!localData) return initialCategoriesLinks[categoryId];

    const { url, ...queryObj } = router.query;
    const { as } = fromObjToUrl(
      { ...values, category_id: categoryId },
      localData,
      queryObj.sorting
    );

    return as;
  };

  return (
    <CategoriesContainer data-cy="categories-container">
      {filledCategories.map(category => (
        <NewSearchCategory
          key={category.id}
          stat={category.searchFormStat}
          icon={category.icon}
          text={category.name}
          onClick={() => handleClick(category.id)}
          isSelected={category.id === selectedValue}
          link={asLinks && generateCategoryUrl(category.id)}
        />
      ))}
    </CategoriesContainer>
  );
};
